// components/industry-templates/CybersecurityEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertTriangle, Lock, Bug } from 'lucide-react';

export const CybersecurityEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Threat Landscape */}
      {industryData?.threat_sessions && (
        <Card className="border-0 bg-gradient-to-br from-red-600/20 to-orange-600/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Shield className="w-8 h-8" style={{ color: theme.primary }} />
              Cyber Threat Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.threat_sessions.map((session: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border-2 border-red-500/30">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">🛡️</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{session.topic}</h3>
                      <p className="text-slate-300 text-sm mb-3">{session.description}</p>
                      <div className="flex gap-2">
                        <Badge style={{ backgroundColor: theme.primary }}>{session.speaker}</Badge>
                        <Badge variant="outline" className="border-white/20 text-white">{session.time}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Capture The Flag */}
      {industryData?.ctf_competition && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Bug className="w-6 h-6" style={{ color: theme.primary }} />
              Capture The Flag (CTF) Competition
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white/10 rounded-xl p-6 text-center">
                <p className="text-4xl font-bold mb-2" style={{ color: theme.primary }}>
                  {industryData.ctf_competition.prize_pool}
                </p>
                <p className="text-white text-xl">Prize Pool</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-white">{industryData.ctf_competition.challenges}</p>
                  <p className="text-xs text-slate-400">Challenges</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-white">{industryData.ctf_competition.duration}</p>
                  <p className="text-xs text-slate-400">Duration</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-lg">
                  <p className="text-2xl font-bold text-white">{industryData.ctf_competition.teams}</p>
                  <p className="text-xs text-slate-400">Max Teams</p>
                </div>
              </div>
              {industryData.ctf_competition.categories && (
                <div className="flex flex-wrap gap-2">
                  {industryData.ctf_competition.categories.map((cat: string, idx: number) => (
                    <Badge key={idx} className="bg-red-500" variant="outline">
                      {cat}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Tools Demo */}
      {industryData?.security_tools && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="w-6 h-6" style={{ color: theme.primary }} />
              Security Tools & Solutions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {industryData.security_tools.map((tool: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{tool.name}</h4>
                  <p className="text-sm text-slate-400 mb-2">{tool.vendor}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>{tool.category}</Badge>
                    {tool.demo && <Badge className="bg-green-500">Live Demo</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Incident Response */}
      {industryData?.incident_response_workshop && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" style={{ color: theme.primary }} />
              Incident Response Workshop
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-4">Hands-on incident response simulation</p>
              <div className="space-y-2">
                {industryData.incident_response_workshop.scenarios?.map((scenario: string, idx: number) => (
                  <div key={idx} className="p-3 bg-white/5 rounded flex items-center gap-2">
                    <span className="text-red-400">⚠️</span>
                    <span className="text-white text-sm">{scenario}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
